import { useCallback, useEffect, useId, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import { isInvoiceFiltersOpenAtom } from "@/store/invoiceAtoms";
import { Button, ErrorState, Icon, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import type { InvoiceKindType, InvoiceStatusType } from "@models/invoice";
import { InvoiceCard } from "./_components/InvoiceCard";
import { InvoiceFiltersDrawer } from "./_components/InvoiceFiltersDrawer";
import { OrdersPagination } from "./_components/OrdersPagination";
import {
    BILLING_HISTORY_SORT_OPTIONS,
    parseBillingHistoryParams,
    toMeInvoicesQuery,
    writeBillingHistoryChoices,
    writeBillingHistoryPage,
    type BillingHistoryChoices,
    type BillingHistorySort,
} from "./_helpers/billingHistoryParams";
import { useMeInvoices } from "./_helpers/useMeInvoices";

const PAGE_SIZE = 10;

// Customer Billing History (#215, E7-S7) at /c/account/billing: the user's own
// order invoices plus their organization's membership invoices, which the API
// scopes. Sort, filters and page live in the URL, so reload and Back restore
// them; the URL drives the request. Every state is distinct — a failed request
// is an error with a retry, never an empty list.
export const BillingHistory = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useMemo(
        () => parseBillingHistoryParams(searchParams),
        [searchParams],
    );
    const query = useMemo(() => toMeInvoicesQuery(params, PAGE_SIZE), [params]);
    const { state, retry } = useMeInvoices(query);
    // The house overlay pattern: the open atom is the drawer's single source
    // of truth. Closed when the page unmounts, so leaving with it open cannot
    // reopen it on the next visit.
    const [isFiltersOpen, setIsFiltersOpen] = useAtom(isInvoiceFiltersOpenAtom);
    useEffect(() => () => setIsFiltersOpen(false), [setIsFiltersOpen]);
    // Stable: BottomSheet keys its keydown listener on onClose.
    const closeFilters = useCallback(
        () => setIsFiltersOpen(false),
        [setIsFiltersOpen],
    );
    const sortId = useId();

    const filterCount = params.kinds.length + params.statuses.length;

    const commit = (next: BillingHistoryChoices) =>
        setSearchParams(writeBillingHistoryChoices(searchParams, next));

    const setSort = (sort: BillingHistorySort) => commit({ ...params, sort });

    const applyFilters = (
        kinds: InvoiceKindType[],
        statuses: InvoiceStatusType[],
    ) => {
        commit({ ...params, kinds, statuses });
        setIsFiltersOpen(false);
    };

    const clearFilters = () => commit({ ...params, kinds: [], statuses: [] });

    const setPage = (page: number) => {
        setSearchParams(writeBillingHistoryPage(searchParams, page));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <h1 className="text-3xl font-bold text-neutral">
                    Billing History
                </h1>

                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <label htmlFor={sortId} className="font-bold">
                            Sort by:
                        </label>
                        <select
                            id={sortId}
                            className="select select-primary select-sm w-auto"
                            value={params.sort}
                            onChange={(event) =>
                                setSort(event.target.value as BillingHistorySort)
                            }
                        >
                            {BILLING_HISTORY_SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline btn-primary btn-sm"
                        aria-label={
                            filterCount > 0
                                ? `Filters, ${filterCount} active`
                                : "Filters"
                        }
                        onClick={() => setIsFiltersOpen(true)}
                    >
                        Filters
                        {filterCount > 0 && (
                            <span className="badge badge-primary badge-sm">
                                {filterCount}
                            </span>
                        )}
                        <Icon name="filter_alt" className="text-lg" />
                    </button>
                </div>

                {state.status === "loading" && (
                    <div
                        role="status"
                        aria-label="Loading invoices"
                        className="flex justify-center items-center py-12"
                    >
                        <Spinner size="lg" />
                    </div>
                )}

                {state.status === "failed" && (
                    <ErrorState
                        message="Couldn't load your invoices."
                        onRetry={retry}
                    />
                )}

                {state.status === "loaded" && state.page.count === 0 && (
                    <div className="flex flex-col items-center gap-4 py-12 text-center">
                        {filterCount > 0 ? (
                            <>
                                <p className="text-neutral/70">
                                    No invoices match these filters.
                                </p>
                                <Button
                                    color={ComponentColor.Primary}
                                    outline
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </Button>
                            </>
                        ) : (
                            <p className="text-neutral/70">
                                No invoices yet. Your order and membership
                                invoices will show up here.
                            </p>
                        )}
                    </div>
                )}

                {state.status === "loaded" && state.page.count > 0 && (
                    <>
                        {state.page.items.length === 0 ? (
                            // A page past the end (a stale ?page= after the
                            // list shrank): there ARE invoices, so neither
                            // empty state is true. No pager here — from
                            // ?page=9 of 3 it would mark no page current and
                            // its Previous would step to 8, still past the end.
                            <div className="flex flex-col items-center gap-4 py-12 text-center">
                                <p className="text-neutral/70">
                                    No invoices on this page.
                                </p>
                                <Button
                                    color={ComponentColor.Primary}
                                    outline
                                    onClick={() => setPage(1)}
                                >
                                    Back to page 1
                                </Button>                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col gap-4">
                                    {state.page.items.map((invoice) => (
                                        <InvoiceCard
                                            key={invoice.id}
                                            invoice={invoice}
                                        />
                                    ))}
                                </div>
                                {state.page.totalPages > 1 && (
                                    <OrdersPagination
                                        pageNumber={state.page.pageNumber}
                                        totalPages={state.page.totalPages}
                                        onPageChange={setPage}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Keyed on the URL: a change under the open drawer (a browser
                Back) remounts it with a draft seeded from the new URL. */}
            {isFiltersOpen && (
                <InvoiceFiltersDrawer
                    key={searchParams.toString()}
                    kinds={params.kinds}
                    statuses={params.statuses}
                    onApply={applyFilters}
                    onClose={closeFilters}
                />
            )}
        </div>
    );
};
